import {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useSearchParams } from "react-router-dom";
import { FunnelSimple, MagnifyingGlass } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { SearchInput } from "./components/SearchInput";
import { FilterChips } from "./components/FilterChips";
import { BulkActions } from "./components/BulkActions";
import { FiltersPanel } from "./components/FiltersPanel";
import { useKeyboardShortcut } from "shared/hooks/useKeyboardShortcut";
import ButtonNew from "components/buttonNew/buttonNew";
import type { FilterBarProps, URLFilterState } from "./types";

export interface FilterBarRef {
  focusSearch: () => void;
  openFilters: () => void;
}

export const FilterBar = forwardRef<FilterBarRef, FilterBarProps>(
  (
    {
      searchValue,
      onSearchChange,
      searchPlaceholder = "Zoeken...",
      filterChips = [],
      primaryButton,
      secondaryButton,
      rightContent,
      rightSlot,
      className = "",
      selectedCount = 0,
      bulkActions = [],
      onClearSelection,
      filterSections = [],
      groupByOptions = [],
      selectedGroupBy = "none",
      onGroupByChange,
      savedSearches = [],
      onSaveCurrentSearch,
      onApplySavedSearch,
      showWhenEmpty = true,
      isLoading = false,
      syncWithURL = false,
      urlParamPrefix = "",
      onFiltersFromURL,
      contextLabel,
    },
    ref,
  ) => {
    const filterBarRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const [showFiltersPanel, setShowFiltersPanel] = useState(false);
    const [isSearching, setIsSearching] = useState(false);

    // URL synchronization
    const [searchParams, setSearchParams] = useSearchParams();
    const urlSyncTimeoutRef = useRef<NodeJS.Timeout>();
    const hasInitializedFromURL = useRef(false);
    const lastURLParamsRef = useRef<string>(""); // Track URL changes
    const isRestoringFromURL = useRef(false); // Prevent update conflicts

    // Combine internal search loading with external loading state
    const isAnyLoading = isSearching || isLoading;

    // Track committed search terms (only when user presses Enter)
    const [committedSearch, setCommittedSearch] = useState("");

    // URL sync utility functions
    const getParamName = (key: string) =>
      urlParamPrefix ? `${urlParamPrefix}_${key}` : key;

    const parseFiltersFromURL = (): URLFilterState => {
      const filters: URLFilterState = {};

      const searchParam = searchParams.get(getParamName("search"));
      if (searchParam) filters.search = searchParam;

      const groupByParam = searchParams.get(getParamName("groupBy"));
      if (groupByParam && groupByParam !== "none")
        filters.groupBy = groupByParam;

      // Parse filter sections
      filters.filterSections = {};
      filterSections.forEach((section) => {
        const paramValue = searchParams.get(getParamName(section.key));
        if (paramValue) {
          filters.filterSections![section.key] = paramValue
            .split(",")
            .filter(Boolean);
        }
      });

      return filters;
    };

    const updateURLWithFilters = () => {
      if (!syncWithURL) return;

      // Prevent URL updates during restoration to avoid conflicts
      if (isRestoringFromURL.current) {
        console.log(`🚫 FilterBar: Skipping URL update during restoration`);
        return;
      }

      // Clear existing timeout
      if (urlSyncTimeoutRef.current) {
        clearTimeout(urlSyncTimeoutRef.current);
      }

      // Debounce URL updates
      urlSyncTimeoutRef.current = setTimeout(() => {
        const newParams = new URLSearchParams(searchParams);

        // Update search parameter
        if (searchValue.trim()) {
          newParams.set(getParamName("search"), searchValue.trim());
        } else {
          newParams.delete(getParamName("search"));
        }

        // Update groupBy parameter
        if (selectedGroupBy && selectedGroupBy !== "none") {
          newParams.set(getParamName("groupBy"), selectedGroupBy);
        } else {
          newParams.delete(getParamName("groupBy"));
        }

        // Update filter section parameters
        filterSections.forEach((section) => {
          const paramName = getParamName(section.key);
          if (section.selectedValues.length > 0) {
            newParams.set(paramName, section.selectedValues.join(","));
          } else {
            newParams.delete(paramName);
          }
        });

        // Update URL without navigation
        setSearchParams(newParams, { replace: true });
      }, 300); // 300ms debounce
    };

    // Initialize from URL on mount AND when URL changes (e.g., browser navigation)
    useEffect(() => {
      if (!syncWithURL || !onFiltersFromURL) return;

      const currentURLParams = searchParams.toString();
      const isFirstLoad = !hasInitializedFromURL.current;
      const hasURLChanged = currentURLParams !== lastURLParamsRef.current;

      // Initialize on first load OR when URL parameters change (e.g., back/forward navigation)
      if (isFirstLoad || hasURLChanged) {
        hasInitializedFromURL.current = true;
        lastURLParamsRef.current = currentURLParams;

        const filtersFromURL = parseFiltersFromURL();

        if (hasURLChanged || isFirstLoad) {
          console.log(
            `🔄 FilterBar: ${isFirstLoad ? "Initial" : "Navigation"} URL restoration:`,
            filtersFromURL,
          );
          isRestoringFromURL.current = true;

          // Synchronize internal committed search state with URL restoration
          if (filtersFromURL.search) {
            setCommittedSearch(filtersFromURL.search);
            console.log(
              `📝 FilterBar: Synchronized committedSearch with URL: "${filtersFromURL.search}"`,
            );
          } else {
            setCommittedSearch("");
            console.log(
              `📝 FilterBar: Cleared committedSearch (no search in URL)`,
            );
          }

          onFiltersFromURL(filtersFromURL);
          // Reset restoration flag after sufficient time for React state updates to complete
          setTimeout(() => {
            isRestoringFromURL.current = false;
            console.log(
              `🔄 FilterBar: URL restoration complete, resuming URL sync`,
            );
          }, 500); // Increased delay to handle complex state chains
        }
      }
    }, [syncWithURL, onFiltersFromURL, searchParams]);

    // Update URL when filters change (but not during restoration)
    useEffect(() => {
      if (hasInitializedFromURL.current && !isRestoringFromURL.current) {
        updateURLWithFilters();
      }
    }, [
      searchValue,
      selectedGroupBy,
      filterChips,
      filterSections,
      syncWithURL,
    ]);

    // Cleanup timeout on unmount
    useEffect(() => {
      return () => {
        if (urlSyncTimeoutRef.current) {
          clearTimeout(urlSyncTimeoutRef.current);
        }
      };
    }, []);

    // Lock body scroll when filter panel is open
    useEffect(() => {
      if (showFiltersPanel) {
        // Save original body overflow
        const originalOverflow = document.body.style.overflow;

        // Lock body scroll
        document.body.style.overflow = "hidden";

        // Restore on cleanup
        return () => {
          document.body.style.overflow = originalOverflow;
        };
      }
    }, [showFiltersPanel]);

    // Expose methods via ref
    useImperativeHandle(
      ref,
      () => ({
        focusSearch: () => {
          searchInputRef.current?.focus();
        },
        openFilters: () => {
          if (filterSections.length > 0) {
            setShowFiltersPanel(true);
          }
        },
      }),
      [filterSections.length],
    );

    // Keyboard shortcuts
    useKeyboardShortcut(
      "shift+f",
      () => {
        searchInputRef.current?.focus();
      },
      [],
      "Focus page search",
      "page",
    );

    useKeyboardShortcut(
      "shift+g",
      () => {
        if (filterSections.length > 0) {
          setShowFiltersPanel(true);
        }
      },
      [filterSections.length],
      "Open filter panel",
      "page",
    );

    // Don't show if no content and not forced to show
    const hasContent =
      searchValue.trim() ||
      filterChips.length > 0 ||
      selectedCount > 0 ||
      primaryButton ||
      secondaryButton ||
      rightContent ||
      rightSlot ||
      filterSections.length > 0;

    if (!hasContent && !showWhenEmpty) {
      return null;
    }

    // Update CSS variable for bottom offset
    useEffect(() => {
      const updateFilterBarHeight = () => {
        const h = filterBarRef.current?.offsetHeight || 0;
        document.documentElement.style.setProperty(
          "--filter-bar-height",
          `${h}px`,
        );
      };

      updateFilterBarHeight();
      let ro: ResizeObserver | null = null;
      try {
        // @ts-ignore
        ro = new ResizeObserver(() => updateFilterBarHeight());
        if (filterBarRef.current) ro.observe(filterBarRef.current);
      } catch {}

      window.addEventListener("resize", updateFilterBarHeight);
      return () => {
        window.removeEventListener("resize", updateFilterBarHeight);
        if (ro && filterBarRef.current) ro.unobserve(filterBarRef.current);
        // Intentionally NOT resetting --filter-bar-height here.
        // The spacer that consumes this variable lives inside this component
        // (see end of return), so a real unmount removes the spacer with it.
        // In React 18 StrictMode (dev) every effect runs mount→cleanup→mount;
        // resetting to "0px" in cleanup caused the spacer to briefly collapse
        // and the table to reflow upward, producing a visible single-frame flicker.
      };
    }, []);

    return (
      <>
        {/* Main Filter Bar - Bottom Sticky */}
        <div
          ref={filterBarRef}
          className={cn(
            "bg-background/60 border-border fixed right-0 bottom-0 left-0 z-50 overflow-visible border-t backdrop-blur-lg",
            className,
          )}
        >
          <div className="relative z-10 w-full px-4 py-4">
            <div className="grid grid-cols-1 items-center gap-4 lg:grid-cols-3">
              {/* Left: Action Buttons (Normal or Bulk) */}
              <div className="relative z-20 order-2 flex min-w-0 items-center justify-center gap-2 lg:order-1 lg:justify-start">
                {selectedCount > 0 ? (
                  <BulkActions
                    selectedCount={selectedCount}
                    bulkActions={bulkActions}
                    onClearSelection={onClearSelection}
                  />
                ) : (
                  // Normal Actions Mode - keep ButtonNew for consistency
                  <>
                    {primaryButton && (
                      <div className="origin-left scale-90">
                        <ButtonNew
                          variant={primaryButton.variant || "primaryMoss"}
                          onClick={primaryButton.onClick}
                          icon={primaryButton.icon}
                          label={primaryButton.label}
                        />
                      </div>
                    )}
                    {secondaryButton && (
                      <div className="origin-left scale-90">
                        <ButtonNew
                          variant={
                            secondaryButton.variant || "secondaryNeutral"
                          }
                          onClick={secondaryButton.onClick}
                          icon={secondaryButton.icon}
                          label={secondaryButton.label}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Center: Search Bar with Filter Chips */}
              <div className="order-1 flex min-w-0 justify-center lg:order-2">
                <div className="w-full max-w-4xl">
                  <div className="border-input bg-background flex items-center gap-2 rounded-lg border px-3 py-2 shadow-sm">
                    {/* Context Label - Shows which section filters apply to */}
                    {contextLabel && (
                      <Badge
                        variant="outline"
                        className="bg-neutral-100 text-neutral-600 border-neutral-300 shrink-0 font-medium"
                      >
                        {contextLabel}
                      </Badge>
                    )}
                    <span className="text-muted-foreground shrink-0">
                      {isAnyLoading ? (
                        <Spinner className="h-[18px] w-[18px]" />
                      ) : (
                        <MagnifyingGlass className="h-[18px] w-[18px]" />
                      )}
                    </span>
                    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                      {/* Search Chip - Show search term as removable chip only when committed */}
                      {committedSearch.trim() && (
                        <Badge
                          variant="outline"
                          className="cursor-pointer gap-1 border-blue-300 bg-blue-500/10 text-blue-700 hover:bg-blue-500/20"
                          onClick={() => {
                            setCommittedSearch("");
                            onSearchChange("");
                          }}
                          title={`Remove search: ${committedSearch}`}
                        >
                          <MagnifyingGlass className="h-3 w-3" />
                          <span className="max-w-[120px] truncate">
                            "{committedSearch}"
                          </span>
                          <span className="text-sm">×</span>
                        </Badge>
                      )}
                      {/* Group By Chip - Show when grouping is active */}
                      {selectedGroupBy &&
                        selectedGroupBy !== "none" &&
                        groupByOptions.length > 0 && (
                          <Badge
                            variant="outline"
                            className="bg-moss-primary/10 text-moss-primary border-moss-primary/30 hover:bg-moss-primary/20 cursor-pointer gap-1"
                            onClick={() => onGroupByChange?.("none")}
                            title="Remove grouping"
                          >
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              className="shrink-0"
                            >
                              <path
                                d="M3 6h18M3 12h18M3 18h18"
                                className="stroke-current"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <span className="max-w-[120px] truncate">
                              Groeperen:{" "}
                              {groupByOptions.find(
                                (opt) => opt.key === selectedGroupBy,
                              )?.label || selectedGroupBy}
                            </span>
                            <span className="text-sm">×</span>
                          </Badge>
                        )}
                      {/* Filter Chips */}
                      <FilterChips filterChips={filterChips} />
                      {/* Search Input */}
                      <SearchInput
                        ref={searchInputRef}
                        searchValue={searchValue}
                        onSearchChange={onSearchChange}
                        searchPlaceholder={searchPlaceholder}
                        onLoadingChange={setIsSearching}
                        onCommitSearch={(term) => setCommittedSearch(term)}
                        showAsChip={committedSearch.trim().length > 0}
                      />
                    </div>
                    {/* Filter Button with Popover */}
                    {filterSections.length > 0 && (
                      <Popover
                        open={showFiltersPanel}
                        onOpenChange={setShowFiltersPanel}
                      >
                        <PopoverTrigger asChild>
                          <Button
                            variant={showFiltersPanel ? "default" : "ghost"}
                            size="sm"
                            className={cn(
                              "h-8 shrink-0",
                              showFiltersPanel &&
                                "bg-brick-primary/10 text-brick-primary hover:bg-brick-primary/20",
                            )}
                          >
                            <FunnelSimple className="mr-1 h-4 w-4" />
                            Filter
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent
                          className="max-h-[600px] w-full max-w-[1000px] overflow-hidden p-0"
                          align="center"
                          side="top"
                          sideOffset={8}
                        >
                          <FiltersPanel
                            filterSections={filterSections}
                            groupByOptions={groupByOptions}
                            selectedGroupBy={selectedGroupBy}
                            onGroupByChange={onGroupByChange}
                            savedSearches={savedSearches}
                            onSaveCurrentSearch={onSaveCurrentSearch}
                            onApplySavedSearch={onApplySavedSearch}
                            onClose={() => setShowFiltersPanel(false)}
                          />
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                </div>
              </div>

              {/* Right: Custom Content */}
              <div className="order-3 flex min-w-0 items-center justify-center gap-2 lg:justify-end">
                {rightContent}
                {rightSlot}
              </div>
            </div>
          </div>
        </div>

        {/* Spacer to prevent content from being hidden behind the fixed filter bar.
            Always rendered — a fixed bottom bar always needs a spacer to prevent
            the last rows of tables/content from being hidden underneath it. */}
        <div style={{ height: "var(--filter-bar-height, 80px)" }} />
      </>
    );
  },
);
